import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  FilePlus2,
  FolderKanban,
  MapPin,
  Orbit,
  Search,
  Satellite,
} from "lucide-react"
import { useMemo, useState } from "react"

import { OrderStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { Order, OrderKind } from "@/lib/domain"
import { ordersQueryOptions } from "@/lib/query-options"
import { cn } from "@/lib/utils"

type OrderFilter = "All" | OrderKind

export const Route = createFileRoute("/portal/orders")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(ordersQueryOptions()),
  head: () => ({
    meta: [{ title: "Orders · Service Portal" }],
  }),
  component: OrdersPage,
})

function OrdersPage() {
  const { data } = useSuspenseQuery(ordersQueryOptions())
  const [filter, setFilter] = useState<OrderFilter>("All")
  const [query, setQuery] = useState("")
  const [selectedOrderId, setSelectedOrderId] = useState(
    data.orders.at(0)?.id ?? null
  )
  const filteredOrders = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return data.orders.filter((order) => {
      const matchesKind = filter === "All" || order.kind === filter
      const matchesQuery =
        !normalized ||
        [
          order.id,
          order.title,
          order.purpose,
          order.areaLabel,
          order.requestedSatellite,
        ].some((value) => value.toLowerCase().includes(normalized))
      return matchesKind && matchesQuery
    })
  }, [data.orders, filter, query])
  const selectedOrder =
    filteredOrders.find((order) => order.id === selectedOrderId) ??
    filteredOrders.at(0) ??
    null

  return (
    <div className="mx-auto max-w-[1450px]">
      <WorkspacePageHeader
        kicker="Request history"
        title="Archive and tasking orders"
        description="Follow every synthetic request from submission through review, preparation, release, and completion."
        actions={
          <Button render={<Link to="/portal/explorer" />}>
            <FilePlus2 />
            Create request
          </Button>
        }
      />

      <div className="mt-6 grid min-h-[690px] overflow-hidden rounded-[14px] border border-border bg-white xl:grid-cols-[minmax(0,1fr)_430px]">
        <section className="min-w-0 border-b border-border xl:border-r xl:border-b-0">
          <div className="flex flex-col gap-4 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div
              className="flex w-fit rounded-[10px] bg-[var(--cloud)] p-1"
              role="group"
              aria-label="Filter orders by type"
            >
              {(["All", "Archive", "New tasking"] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setFilter(item)}
                  className={cn(
                    "min-h-11 rounded-[8px] px-3 text-xs font-semibold transition-colors focus-visible:outline-3 focus-visible:outline-[var(--cyan)]",
                    filter === item
                      ? "bg-white text-[var(--navy)] shadow-[0_4px_12px_-9px_rgba(18,58,90,.8)]"
                      : "text-muted-foreground hover:text-[var(--ink)]"
                  )}
                  aria-pressed={filter === item}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:max-w-[300px]">
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search request or area"
                aria-label="Search orders"
                className="h-11 rounded-[10px] border-border bg-white pr-9"
              />
              <Search className="pointer-events-none absolute top-3 right-3 size-4 text-muted-foreground" />
            </div>
          </div>

          <div className="border-b border-border bg-[var(--cloud)] px-4 py-2.5 text-[0.65rem] font-medium text-muted-foreground">
            {filteredOrders.length} request
            {filteredOrders.length === 1 ? "" : "s"} · synthetic demo records
          </div>

          {filteredOrders.length > 0 ? (
            <div className="divide-y divide-border">
              {filteredOrders.map((order) => (
                <OrderListItem
                  key={order.id}
                  order={order}
                  selected={selectedOrder?.id === order.id}
                  onSelect={() => setSelectedOrderId(order.id)}
                />
              ))}
            </div>
          ) : (
            <div className="px-6 py-20 text-center">
              <FolderKanban className="mx-auto size-8 text-[var(--telemetry)]" />
              <h2 className="mt-4 font-semibold text-[var(--navy)]">
                No requests match
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Clear the search or choose another request type.
              </p>
            </div>
          )}
        </section>

        <aside className="min-w-0 bg-[#fbfcfc]">
          {selectedOrder ? (
            <OrderDetail order={selectedOrder} />
          ) : (
            <div className="grid min-h-[480px] place-items-center p-8 text-center">
              <div>
                <FolderKanban className="mx-auto size-8 text-[var(--telemetry)]" />
                <p className="mt-3 text-sm text-muted-foreground">
                  Select a request to inspect its details.
                </p>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

function OrderListItem({
  order,
  selected,
  onSelect,
}: {
  order: Order
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "grid w-full gap-4 p-4 text-left transition-colors focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-[var(--cyan)] sm:grid-cols-[1fr_150px_150px] sm:items-center sm:p-5",
        selected ? "bg-[#eef7f8]" : "hover:bg-[var(--cloud)]"
      )}
      aria-pressed={selected}
    >
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-[var(--navy)]">
            {order.title}
          </span>
          <OrderStatusBadge status={order.status} />
        </span>
        <span className="mt-1.5 block truncate text-xs text-muted-foreground">
          {order.areaLabel}
        </span>
        <span className="mt-1 block font-mono text-[0.62rem] text-[var(--telemetry)]">
          {order.id}
        </span>
      </span>
      <span className="text-xs">
        <span className="block font-semibold text-[var(--ink)]">
          {order.kind}
        </span>
        <span className="mt-1 block text-muted-foreground">
          {order.requestedSatellite}
        </span>
      </span>
      <span className="text-xs">
        <span className="block font-semibold text-[var(--ink)]">
          {order.submittedLabel}
        </span>
        <span className="mt-1 block text-muted-foreground">
          Updated {order.updatedLabel}
        </span>
      </span>
    </button>
  )
}

function OrderDetail({ order }: { order: Order }) {
  return (
    <div className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[0.68rem] font-semibold text-[var(--service-blue)]">
          {order.id}
        </p>
        <OrderStatusBadge status={order.status} />
      </div>
      <h2 className="mt-4 text-2xl leading-[1.08] font-semibold tracking-[-0.025em] text-[var(--navy)]">
        {order.title}
      </h2>
      <p className="mt-3 text-sm leading-6 text-muted-foreground">
        {order.purpose}
      </p>

      <div className="mt-6 grid gap-4 rounded-[12px] border border-border bg-white p-4">
        <DetailRow
          icon={order.kind === "New tasking" ? Orbit : Satellite}
          label="Request type"
          value={`${order.kind} · ${order.requestedSatellite}`}
        />
        <DetailRow
          icon={MapPin}
          label="Area of interest"
          value={order.areaLabel}
        />
        <DetailRow
          icon={CalendarDays}
          label="Capture window"
          value={order.dateRange}
        />
      </div>

      <div className="mt-6">
        <p className="text-xs font-semibold text-[var(--navy)]">
          Request progress
        </p>
        <ol className="mt-4 grid gap-0">
          {[
            ["Submitted", true],
            [
              "Technical review",
              ["review", "approved", "ready", "completed"].includes(
                order.status
              ),
            ],
            [
              "Approved",
              ["approved", "ready", "completed"].includes(order.status),
            ],
            [
              order.kind === "Archive"
                ? "Package prepared"
                : "Acquisition planned",
              ["ready", "completed"].includes(order.status),
            ],
          ].map(([label, reached], index) => (
            <li key={String(label)} className="grid grid-cols-[22px_1fr] gap-3">
              <span className="relative flex justify-center">
                <span
                  className={cn(
                    "relative z-10 mt-0.5 size-3 rounded-full border-2",
                    reached
                      ? "border-[var(--action)] bg-[var(--action)]"
                      : "border-[#bdc9cd] bg-white"
                  )}
                />
                {index < 3 ? (
                  <span className="absolute top-3 bottom-0 w-px bg-border" />
                ) : null}
              </span>
              <div className="pb-5">
                <p
                  className={cn(
                    "text-xs font-semibold",
                    reached ? "text-[var(--ink)]" : "text-muted-foreground"
                  )}
                >
                  {String(label)}
                </p>
                {index === 0 ? (
                  <p className="mt-1 text-[0.65rem] text-muted-foreground">
                    {order.submittedLabel} · {order.requester}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-2 border-t border-border pt-5">
        <p className="text-xs font-semibold text-[var(--navy)]">
          Technical notes
        </p>
        <p className="mt-2 text-xs leading-6 text-muted-foreground">
          {order.notes}
        </p>
      </div>

      <Button
        render={<Link to="/portal/explorer" />}
        variant="outline"
        className="mt-6 w-full"
      >
        Return to imagery explorer
        <ArrowRight />
      </Button>
    </div>
  )
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Satellite
  label: string
  value: string
}) {
  return (
    <div className="flex gap-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-[8px] bg-[#e8f4f7] text-[var(--service-blue)]">
        <Icon className="size-3.5" />
      </span>
      <div>
        <p className="text-[0.62rem] font-medium text-muted-foreground">
          {label}
        </p>
        <p className="mt-1 text-xs font-semibold text-[var(--ink)]">{value}</p>
      </div>
    </div>
  )
}
